import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {HTMLDialogElement|null} */
  #dialogEl = null;
  /** @type {HTMLElement|null} */
  #pageBlurEl = null;
  /** @type {number} */
  #trustRetries = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    // Page-blur overlay follows the dialog open/close (all paths: trigger, add, ESC, scrim, close button)
    this.addEventListener(DialogOpenEvent.eventName, this.#showPageBlur);
    this.#dialogEl = this.querySelector('.cart-drawer__dialog');
    this.#dialogEl?.addEventListener('close', this.#hidePageBlur);
    // Move the blur overlay to <body> so a transformed/sticky header ancestor can't
    // clip its position:fixed (it was being confined to the ~59px header height).
    this.#pageBlurEl = this.querySelector('.cart-page-blur');
    if (this.#pageBlurEl && this.#pageBlurEl.parentElement !== document.body) {
      document.body.appendChild(this.#pageBlurEl);
    }
    // The cart section re-renders on every update and resets the trust row to static
    // (2-line) spans — rebuild the single-line marquee after the DOM settles.
    document.addEventListener('cart:update', this.#onCartUpdated);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#showPageBlur);
    this.#dialogEl?.removeEventListener('close', this.#hidePageBlur);
    document.removeEventListener('cart:update', this.#onCartUpdated);
  }

  #onCartUpdated = () => {
    // Rebuild the single-line trust marquee after the morph regenerates .cart-trust as static
    // spans. Fast path (rAF) for the synchronous morph (sections= in the response), plus a
    // setTimeout fallback for the async re-fetch path. #buildTrustMarquee is idempotent.
    requestAnimationFrame(() => requestAnimationFrame(() => this.#buildTrustMarquee()));
    // Fallback for the ASYNC re-fetch morph (no sections= in payload): it replaces .cart-trust
    // ~200-500ms later, wiping any marquee built before it. 600ms runs safely after the morph.
    setTimeout(() => this.#buildTrustMarquee(), 600);
    // Recover focus if the morph dropped it to <body> (keyboard / screen-reader users).
    requestAnimationFrame(() => {
      if (document.activeElement === document.body && this.#dialogEl?.open) {
        /** @type {HTMLElement|null} */ (this.querySelector('.cart-drawer__close-button'))?.focus();
      }
    });
  };

  #showPageBlur = () => {
    (this.#pageBlurEl || document.querySelector('.cart-page-blur'))?.classList.add('is-active');
    // Tell the inline upsell loader to populate "You might also like" on open (icon click),
    // not only on cart:update (add/qty change).
    document.dispatchEvent(new CustomEvent('onne:cart-drawer:open'));
  };

  #hidePageBlur = () => {
    (this.#pageBlurEl || document.querySelector('.cart-page-blur'))?.classList.remove('is-active');
  };

  #handleCartAdd = (event) => {
    // Do NOT open the drawer on a failed add (OOS / stock limit / /cart/add error):
    // product-form emits CartAddEvent with detail.data.didError on failure, and the
    // product form shows the correct inline message. (Codex P0)
    if (event?.detail?.data?.didError) return;
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
      this.#showPageBlur();
      requestAnimationFrame(() => {
        this.#buildTrustMarquee();
      });
    }
  };

  /**
   * Turn the trust row (Free Returns · Worldwide Shipping · Secure Payments) into a
   * single-line rotating marquee, reusing the global header marquee technique
   * (.ann-mq / .ann-mq__track / @keyframes ann-mq-scroll: translateX 0 → -50% with two
   * cloned groups for a seamless loop). Idempotent.
   */
  #buildTrustMarquee = () => {
    const trust = this.querySelector('.cart-trust');
    if (!trust || trust.querySelector('.ann-mq')) return;

    let labels = Array.from(trust.querySelectorAll('span'))
      .map((s) => (s.textContent || '').replace(/\s+/g, ' ').trim())
      .filter((t) => t && t.length > 1 && !/^[·•|]$/.test(t));
    if (!labels.length) {
      labels = (trust.textContent || '')
        .replace(/\s+/g, ' ')
        .split(/\s*[·•|]\s*/)
        .map((t) => t.trim())
        .filter(Boolean);
    }
    if (!labels.length) return;

    // FIXED repetitions + FIXED calm duration (no width-measurement — it raced the modal
    // layout: clientWidth/offsetWidth were 0 at build time, producing a 600-item track at
    // light speed). Two identical groups loop seamlessly via translateX(0 → -50%). 28s ≈ the
    // header marquee pace for this short, fixed copy.
    const REPS = 4;
    const box = document.createElement('div');
    box.className = 'ann-mq cart-trust__mq';
    const track = document.createElement('div');
    track.className = 'ann-mq__track';
    const g1 = document.createElement('div');
    g1.className = 'ann-mq__group';
    const seq = labels.flatMap((l) => [l, '·']);
    for (let r = 0; r < REPS; r++) {
      seq.forEach((txt) => {
        const s = document.createElement('span');
        s.className = 'ann-mq__item';
        s.textContent = txt;
        g1.appendChild(s);
      });
    }
    track.appendChild(g1);
    const g2 = g1.cloneNode(true);
    g2.setAttribute('aria-hidden', 'true');
    track.appendChild(g2);
    box.appendChild(track);
    track.style.setProperty('--ann-mq-duration', '28s');
    trust.textContent = '';
    trust.appendChild(box);
  };

  open() {
    this.showDialog();
    this.#showPageBlur();
    this.#buildTrustMarquee();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
    this.#hidePageBlur();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
